/**
 * YouTubeEmbed — full-width responsive YouTube video iframe with a clean
 * frame and an optional "Play backing track" toggle. Used for both the
 * official song video and instrumental backing tracks for practice.
 */

interface YouTubeEmbedProps {
  /** YouTube video ID (the part after `v=` in the URL). */
  videoId: string;
  /** Optional title shown above the video. */
  title?: string;
  /** Optional caption shown below. */
  caption?: string;
  /** Optional second video — typically the backing track. */
  backingTrackId?: string;
  backingTrackLabel?: string;
}

export function YouTubeEmbed({
  videoId,
  title,
  caption,
  backingTrackId,
  backingTrackLabel = "Backing track for practice",
}: YouTubeEmbedProps) {
  return (
    <div className="mx-auto max-w-5xl px-4 lg:px-8 my-5 space-y-3">
      <figure className="overflow-hidden rounded-2xl border border-amber-500/20 bg-zinc-900/50 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
        {title && (
          <div className="border-b border-nsm-border bg-zinc-900/70 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300">
            {title}
          </div>
        )}
        <div className="aspect-video w-full">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0`}
            title={title ?? "Song video"}
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        {caption && (
          <figcaption className="border-t border-nsm-border bg-zinc-900/40 px-4 py-2 text-[12px] italic text-zinc-400">
            {caption}
          </figcaption>
        )}
      </figure>
      {backingTrackId && (
        <details className="overflow-hidden rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.04]">
          <summary className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm font-semibold text-emerald-200 hover:bg-emerald-500/10">
            <span>🎹</span>
            <span>{backingTrackLabel}</span>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-emerald-400/70">
              click to expand
            </span>
          </summary>
          <div className="aspect-video w-full">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${backingTrackId}?rel=0`}
              title="Backing track"
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </details>
      )}
    </div>
  );
}
